import { useToast } from "@/hooks/useToast";
import { useState } from "react";
import { uploadImage } from "@/server/services/imageService";
import {
  deleteUserAvatar,
  isUserAuthenticated,
  updateUserAvatar,
} from "../auth";
import { useTranslation } from "react-i18next";

import { AVATAR_SIZE, MAX_AVATAR_SIZE_MB, MB } from "@/config/constants";
import {
  createImageThumbnail,
  getImageMetadata,
} from "@/utils/createThumbnail";

export interface UseAvatarUploaderReturn {
  isUploading: boolean;
  blobUrl: string;
  attachmentId: string;
  /** True when the user explicitly clicked "remove avatar" (for chat avatar forms) */
  avatarCleared: boolean;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  discardBlobUrl: () => void;
  /** Calls deleteUserAvatar API — use for user profile avatar only */
  deleteAvatar: () => Promise<void>;
  /** Clears local preview state without calling any API — use for chat avatar forms
   *  where the actual deletion happens on form submit */
  clearAvatar: () => void;
}

type Props = {
  updateUserAvatarCallback?: boolean;
  updateChatAvatarCallback?: boolean;
};

// TO DO => need improvement
// check if return cleanup needed on revoking blob urls (we do that on removeAvatar now)
// - add delete attachment id
// - check with team for better approach

export function useAvatarUploader({
  updateUserAvatarCallback,
  // updateChatAvatarCallback,
}: Props): UseAvatarUploaderReturn {
  const { toast } = useToast();
  const { t } = useTranslation();

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [blobUrl, setBlobUrl] = useState<string>("");
  const [attachmentId, setAttachmentId] = useState<string>("");
  const [avatarCleared, setAvatarCleared] = useState<boolean>(false);

  const discardBlobUrl = () => {
    URL.revokeObjectURL(blobUrl);
    setAttachmentId("");
    setBlobUrl("");
    setAvatarCleared(false);
  };

  const clearAvatar = () => {
    discardBlobUrl();
    setAvatarCleared(true);
  };

  const deleteAvatar = async () => {
    try {
      setIsUploading(true);

      // TO DO => remove the bucket file from storage also
      // TO DO => handle both user and chat avatars

      await deleteUserAvatar();

      discardBlobUrl();
      toast({
        duration: 1500,
        description: t("avatar.removedSuccessfully"),
      });
    } catch (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    // extra check if auth is present
    const userFingerPrint = isUserAuthenticated();
    if (!userFingerPrint) {
      return;
    }

    // TO DO => add delete attachment id func
    // check for previous stored attachment id
    let file = e.target.files?.[0];
    if (!file) {
      return;
    }

    // Check file size limit
    if (file.size > MAX_AVATAR_SIZE_MB) {
      toast({
        duration: 1500,
        description: `File size exceeds the limit of ${MAX_AVATAR_SIZE_MB / MB}MB`,
        variant: "destructive",
      });
      return;
    }

    // Create a blob URL for the optimistic (preview)
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    setAvatarCleared(false);

    try {
      setIsUploading(true);

      const { width, height } = await getImageMetadata(file);

      if (width >= AVATAR_SIZE || height >= AVATAR_SIZE) {
        const thumbnail = await createImageThumbnail({
          file,
          maxWidth: AVATAR_SIZE,
          maxHeight: AVATAR_SIZE,
          quality: 1,
        });
        if (thumbnail) {
          file = new File([thumbnail], file.name, { type: file.type });
        }
      }

      const _attachmentId = await uploadImage(file, file.name);

      if (!_attachmentId) {
        toast({
          duration: 1500,
          description: t("avatar.errorUploadingMissingId"),
          variant: "destructive",
        });
        discardBlobUrl();

        return;
      }

      setAttachmentId(_attachmentId);

      if (updateUserAvatarCallback) {
        await updateUserAvatar(_attachmentId);
        toast({
          duration: 1500,
          description: t("avatar.updatedSuccessfully"),
        });
      }
    } catch (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : "Error uploading avatar",
        variant: "destructive",
      });
      discardBlobUrl();
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    blobUrl,
    attachmentId,
    avatarCleared,
    handleFileChange,
    discardBlobUrl,
    deleteAvatar,
    clearAvatar,
  };
}
